import clsx from 'clsx';
import React from 'react';
import { HUE_TINT, type Hue, isHue } from './hue';

/**
 * Status is communicated with a dot + text, never colour alone.
 * Tones are semantic; the mapping from domain state → tone lives in the
 * feature module, not here.
 */
export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

const dotClass: Record<Tone, string> = {
  neutral: 'bg-v2-fg-4',
  accent: 'bg-v2-accent',
  success: 'bg-v2-success',
  warning: 'bg-v2-warning',
  danger: 'bg-v2-danger',
};

const textClass: Record<Tone, string> = {
  neutral: 'text-v2-fg-2',
  accent: 'text-v2-fg',
  success: 'text-v2-fg',
  warning: 'text-v2-fg',
  danger: 'text-v2-fg',
};

export const StatusDot: React.FC<{
  tone: Tone;
  hollow?: boolean;
  className?: string;
}> = ({ tone, hollow, className }) => (
  <span
    aria-hidden
    className={clsx(
      'inline-block h-2 w-2 shrink-0 rounded-full',
      hollow
        ? clsx('border-[1.5px] bg-transparent', {
            'border-v2-fg-4': tone === 'neutral',
            'border-v2-accent': tone === 'accent',
            'border-v2-success': tone === 'success',
            'border-v2-warning': tone === 'warning',
            'border-v2-danger': tone === 'danger',
          })
        : dotClass[tone],
      className,
    )}
  />
);

export const StatusLabel: React.FC<{
  tone: Tone;
  children: React.ReactNode;
  hollow?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}> = ({ tone, children, hollow, className, size = 'md' }) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1.5 whitespace-nowrap font-medium',
      size === 'sm' ? 'text-v2-12' : 'text-v2-13',
      textClass[tone],
      className,
    )}
  >
    <StatusDot tone={tone} hollow={hollow} />
    {children}
  </span>
);

export type ChipTone = Tone | Hue;

/**
 * One capsule for order type, order status, guest tags and intent.
 * Padding and radius stay put; `caps` only changes case and weight, the way
 * operational chips (PICKUP, COMPLETED) sit beside sentence-case guest tags.
 */
const CAPSULE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-[3px] leading-none';
const CAPSULE_TYPE = 'text-v2-11 font-semibold';
const CAPSULE_CAPS = 'text-[10.5px] font-bold uppercase tracking-[0.05em]';

const chipToneClass = (tone: ChipTone) => {
  if (tone === 'neutral') return 'bg-v2-sunken text-v2-fg-3';
  if (tone === 'success') return 'bg-v2-success/10 text-v2-success';
  // Amber, shared by in-progress orders and VIP guests.
  if (tone === 'warning') return 'bg-v2-amber/[0.14] text-v2-amber';
  if (tone === 'danger') return 'bg-v2-danger/10 text-v2-danger';
  if (tone === 'accent') return 'bg-v2-accent/[0.12] text-v2-accent';
  if (isHue(tone)) return HUE_TINT[tone];
  return 'bg-v2-sunken text-v2-fg-3';
};

/**
 * StatusPill: the shared capsule plus a dot. For guest-facing operational
 * states (a reservation's Confirmed / Modified / Cancelled) where the state
 * has to be found at a glance. The dot carries the meaning; the tint matches
 * `Tag`. Order and customer chips use `Tag` — same capsule, no dot.
 */
export const StatusPill: React.FC<{
  tone: Tone;
  children: React.ReactNode;
  /** Waiting states: hollow dot, as on `StatusLabel`. */
  hollow?: boolean;
  className?: string;
}> = ({ tone, children, hollow, className }) => (
  <span
    className={clsx(
      CAPSULE,
      CAPSULE_TYPE,
      'gap-1.5',
      chipToneClass(tone),
      className,
    )}
  >
    <StatusDot tone={tone} hollow={hollow} />
    {children}
  </span>
);

/**
 * Tag: the shared capsule without a dot. Categorical chips (type, channel,
 * guest) and the order-status chip. A categorical `Hue` colours a category
 * (see `hue.ts`); a semantic `Tone` colours a state.
 */
export const Tag: React.FC<{
  children: React.ReactNode;
  tone?: ChipTone;
  /**
   * Operational caps: 10.5px, bold, tracked. Same padding and radius as a
   * sentence-case tag, so intent and order chips line up with guest tags.
   */
  quiet?: boolean;
  /** Uppercase tracking, same box and colour. For operational chips. */
  caps?: boolean;
  className?: string;
}> = ({ children, tone = 'neutral', quiet, caps, className }) => (
  <span
    className={clsx(
      CAPSULE,
      quiet || caps ? CAPSULE_CAPS : CAPSULE_TYPE,
      chipToneClass(tone),
      className,
    )}
  >
    {children}
  </span>
);

const countClass =
  'v2-num inline-flex h-4 min-w-[16px] items-center justify-center rounded-v2-sm bg-v2-sunken px-1 text-v2-11 font-medium text-v2-fg-3';

/** Counter next to a filter / tab label. */
export const Count: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => <span className={clsx(countClass, 'ml-1.5', className)}>{value}</span>;

/**
 * Counter on a navigation item (unread messages, pending approvals). Absent
 * at 0 — a nav item never carries an empty badge — and always named, because
 * a bare number beside a link says nothing when it is read out.
 */
export const NavCount: React.FC<{
  value: number;
  what?: string;
  className?: string;
}> = ({ value, what = 'unread', className }) => {
  if (value <= 0) return null;
  return (
    <span
      aria-label={`${value} ${what}`}
      className={clsx(
        'v2-num inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-v2-warning/[0.14] px-1.5 text-v2-12 font-semibold text-v2-warning',
        className,
      )}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
};
